import Phaser from 'phaser';
import { loadContent } from '../../data/content';
import { createRun, FIXED_DT, type Run, type RunOptions, type Snapshot } from '../../core/run';
import type { CoreEvent, InputFrame } from '../../core/types';
import { createFixedLoop } from '../loop';
import { createInputSource, createKeyTracker, type InputSource, type KeyTracker } from '../input';
import { createTouchControls, isCoarsePointer, type TouchControls } from '../touch';
import { clearTouchSpells } from '../touchSpells';
import { autoHitRange, hasAutoAimTarget } from '../autoFire';
import { DEBUG, publishDebug } from '../debug';
import { isPortrait, shouldRunSimulation } from '../orientation';
import { destroyViews, syncEntities, type Views } from '../render/EntityView';
import { meleeArcDegrees } from '../render/arc';
import { buildMapView, type MapView } from '../render/MapView';
import { buildDoorViews, type DoorViews } from '../render/DoorView';
import { queueRoomAssets, registerAnimations, type Manifest } from '../assets';
import { hudState } from '../ui/state';
import { modifierNames } from '../meta';
import { getAudio } from '../audio';

const content = loadContent();
const PANEL_KEYS = ['Digit1', 'Digit2', 'Digit3', 'Digit4', 'Escape', 'KeyC'];

export class RoomScene extends Phaser.Scene {
  private run!: Run;
  private loop!: ReturnType<typeof createFixedLoop>;
  private input2!: InputSource;
  private touch!: TouchControls;
  private lastFrame: InputFrame | null = null;
  private paused = false;
  private views: Views = new Map();
  private manifest!: Manifest;
  private keys!: KeyTracker;
  private roomId = '';
  private map: MapView | null = null;
  private background: Phaser.GameObjects.Image | null = null;
  private following = false;
  private doorViews: DoorViews | null = null;
  private prevSnap: Snapshot | null = null;
  private curSnap!: Snapshot;
  private roomChanged = false;
  /** Portée à laquelle l'Autohit (mobile) se déclenche : celle du sort principal de la classe. */
  private autoRange = 900;
  /** Atlas de la salle en cours de chargement : simulation et vues suspendues derrière un écran de transition. */
  private loading = false;
  private overlay: Phaser.GameObjects.GameObject[] = [];
  constructor() { super('Room'); }
  create(opts: RunOptions): void {
    this.views.clear();
    this.prevSnap = null;
    this.roomChanged = false;
    this.manifest = this.registry.get('manifest') as Manifest;
    this.run = createRun(content, opts);
    this.autoRange = autoHitRange(content, opts.classId);
    this.roomId = '';
    this.following = false;
    this.keys = createKeyTracker(window);
    this.touch = createTouchControls(this);
    this.lastFrame = null;
    this.paused = false;
    this.input2 = createInputSource(this, this.keys, this.touch, () => hasAutoAimTarget(this.curSnap.entities, this.autoRange));
    hudState.commandSink = (c) => this.run.command(c);
    this.events.once('shutdown', () => {
      this.load.off(Phaser.Loader.Events.COMPLETE);
      this.loading = false;
      this.hideOverlay();
      this.keys.dispose();
      this.input2.dispose();
      this.touch.dispose();
      hudState.commandSink = null;
      destroyViews(this.views);
      this.clearRoom();
      this.clearDoors();
    });
    this.loop = createFixedLoop(() => {
      const frame = this.input2.frame();
      this.lastFrame = frame;
      const events = this.run.step(frame);
      this.onEvents(events);
      hudState.pushEvents(events);
    }, FIXED_DT);
    hudState.reset();
    hudState.modifiers.value = modifierNames(content, opts.bonuses);
    getAudio()?.resetRun();
    this.curSnap = this.run.snapshot();
    this.enterRoom();
  }

  private clearDoors(): void {
    this.doorViews?.destroy();
    this.doorViews = null;
  }

  private clearRoom(): void {
    this.map?.destroy();
    this.map = null;
    this.background?.destroy();
    this.background = null;
  }

  /**
   * Entre dans la salle courante : charge d'abord ce qui manque (atlas de tuiles de la sous-zone, clips du joueur et
   * des monstres de ses vagues) derrière un court écran de transition, puis construit le décor et précharge la salle
   * suivante. Au démarrage seuls les icônes et l'interface sont chargés (BootScene).
   */
  private enterRoom(): void {
    const room = this.run.room();
    this.roomId = room.id;
    this.clearRoom();
    const cam = this.cameras.main;
    cam.stopFollow();
    this.following = false;
    cam.setBounds(0, 0, room.width, room.height);
    cam.centerOn(room.playerStart.x, room.playerStart.y);
    const missing = queueRoomAssets(this.load, this.manifest, this.textures, this.run.roomAssets());
    if (missing.length === 0) { this.buildRoom(room); this.preloadNext(); return; }
    this.loading = true;
    this.showOverlay();
    this.load.once(Phaser.Loader.Events.COMPLETE, () => {
      this.loading = false;
      this.hideOverlay();
      if (this.roomId === room.id) this.buildRoom(room);
      this.preloadNext();
    });
    this.load.start();
  }

  /** Décor de la salle : carte générée (sol en morceaux + objets) ou image de fond (salle à rectangles). */
  private buildRoom(room: ReturnType<Run['room']>): void {
    registerAnimations(this.anims, this.manifest, this.textures); // les atlas arrivés depuis le dernier appel
    this.map = buildMapView(this, room);
    if (!this.map && this.textures.exists(room.map)) this.background = this.add.image(0, 0, room.map).setOrigin(0, 0).setDepth(-100000);
  }

  /** Préchargement silencieux de la salle suivante (le chargeur accepte des fichiers en cours de route). */
  private preloadNext(): void {
    const next = this.run.nextRoomAssets();
    if (next && queueRoomAssets(this.load, this.manifest, this.textures, next).length) this.load.start();
  }

  private showOverlay(): void {
    const cam = this.cameras.main;
    this.overlay = [
      this.add.rectangle(0, 0, cam.width, cam.height, 0x0d0a12, 0.94).setOrigin(0, 0).setScrollFactor(0).setDepth(1e6),
      this.add.text(cam.width / 2, cam.height / 2, 'Chargement de la salle...', { fontFamily: 'Trebuchet MS, sans-serif', fontSize: '22px', color: '#e8dcc0' }).setOrigin(0.5).setScrollFactor(0).setDepth(1e6 + 1),
    ];
  }

  private hideOverlay(): void {
    for (const o of this.overlay) o.destroy();
    this.overlay = [];
  }
  private onEvents(events: CoreEvent[]): void {
    getAudio()?.onEvents(events, { playerId: this.curSnap.entities.find((e) => e.kind === 'player')?.id ?? null, roomsTotal: this.curSnap.roomsTotal });
    for (const e of events) {
      if (e.type === 'roomEntered') {
        // les vues sont recréées : la caméra ne doit plus suivre l'ancienne vue du joueur (détruite)
        destroyViews(this.views);
        this.clearDoors();
        this.cameras.main.stopFollow();
        this.following = false;
        if (e.roomId !== this.roomId) this.enterRoom();
        this.roomChanged = true;
        this.keys.release(PANEL_KEYS);
        clearTouchSpells();
      } else if (e.type === 'doorsOffered') {
        // portes posées sur les sorties de la salle générée (rien pour une salle à rectangles : panneau seul)
        this.clearDoors();
        this.doorViews = buildDoorViews(this, this.run.snapshot().doors);
        this.keys.release(PANEL_KEYS);
        clearTouchSpells();
      } else if (e.type === 'levelUp' || e.type === 'shopOpened' || e.type === 'cardChosen') {
        // Une touche encore enfoncée (choix de carte ou de porte) doit être relâchée puis réappuyée pour agir.
        // Seules les touches de panneaux et de sorts sont relâchées : ZQSD reste enfoncé.
        this.keys.release(PANEL_KEYS);
        clearTouchSpells();
      } else if (e.type === 'invalidCommand') {
        console.warn('Commande ignorée :', e.reason);
      } else if (e.type === 'bossCharge') {
        this.cameras.main.shake(150, 0.004);
      } else if (e.type === 'zoneHit') {
        const c = this.add.circle(e.pos.x, e.pos.y, e.radius, 0xffa040, 0.3).setDepth(e.pos.y + 1);
        this.time.delayedCall(150, () => c.destroy());
      } else if (e.type === 'meleeHit') {
        // part de camembert plein (add.arc ne remplit que le croissant de l'arc)
        const { start, end } = meleeArcDegrees(e.dir, e.halfAngleDeg);
        const g = this.add.graphics().setDepth(e.pos.y + 1);
        g.fillStyle(0xf2d27a, 0.35);
        g.slice(e.pos.x, e.pos.y, e.range, Phaser.Math.DegToRad(start), Phaser.Math.DegToRad(end), false);
        g.fillPath();
        this.time.delayedCall(120, () => g.destroy());
      } else if (e.type === 'pull') {
        const c = this.add.circle(e.pos.x, e.pos.y, e.radius).setStrokeStyle(2, 0xd9c27a, 0.8).setDepth(e.pos.y + 1);
        this.time.delayedCall(150, () => c.destroy());
      }
    }
  }

  update(_t: number, deltaMs: number): void {
    if (this.loading) { this.touch.update(); return; } // écran de transition : rien n'avance tant que les atlas manquent
    let steps = 0;
    // Mobile tenu en portrait : simulation figée derrière l'overlay « Tournez votre téléphone ».
    if (!shouldRunSimulation(isPortrait(), isCoarsePointer())) this.paused = true;
    else {
      if (this.paused) { this.loop.reset(); this.paused = false; } // reprise en paysage : pas de rattrapage
      if (!this.run.done) steps = this.loop.update(deltaMs);
    }
    if (steps > 0) {
      // Après un changement de salle, pas d'interpolation avec l'ancienne salle.
      this.prevSnap = this.roomChanged ? null : this.curSnap;
      this.roomChanged = false;
      this.curSnap = this.run.snapshot();
    }
    this.touch.update();
    this.doorViews?.update(this.cameras.main, this.time.now);
    syncEntities(this, this.curSnap, this.prevSnap, this.loop.alpha(), this.views, this.manifest);
    if (!this.following) {
      // la caméra suit la vue (interpolée) du joueur, recréée à chaque salle
      const pe = this.curSnap.entities.find((e) => e.kind === 'player');
      const view = pe ? this.views.get(pe.id) : undefined;
      if (view) { this.cameras.main.startFollow(view.body, true, 0.12, 0.12); this.following = true; }
    }
    if (DEBUG) {
      const pe = this.curSnap.entities.find((e) => e.kind === 'player');
      publishDebug({ player: pe ? { x: pe.x, y: pe.y, hp: pe.hp } : null, monsters: this.curSnap.entities.filter((e) => e.kind === 'monster' && e.alive).length, input: this.lastFrame, touch: this.touch.enabled(), time: this.curSnap.time, camera: { x: this.cameras.main.scrollX, y: this.cameras.main.scrollY, following: this.following }, room: { id: this.roomId, map: this.map !== null },
        run: { phase: this.curSnap.phase, roomIndex: this.curSnap.roomIndex, boss: this.curSnap.boss?.name ?? null, doors: this.curSnap.doors.map((d) => ({ reward: d.reward, x: d.pos?.x ?? NaN, y: d.pos?.y ?? NaN })), monsterPos: this.curSnap.entities.filter((e) => e.kind === 'monster' && e.alive).map((e) => ({ x: e.x, y: e.y })), pets: this.curSnap.entities.filter((e) => e.kind === 'pet' && e.alive).length } });
    }
    hudState.fromSnapshot(this.curSnap);
    // Autohit (mobile) : le tir part tout seul tant qu'un monstre est à portée de la visée automatique
    hudState.autohit.value = this.touch.enabled() ? (hasAutoAimTarget(this.curSnap.entities, this.autoRange) ? 'on' : 'idle') : null;
    if (this.run.done && !hudState.ended.value) {
      const summary = this.run.summary();
      this.game.events.emit('run:ended', summary);
      hudState.end(summary);
    }
  }
}
